import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  IdChip,
  Input,
  Pagination,
  Select,
  Skeleton,
} from '../components/ui';
import { api, compact } from '../lib/api';
import { TYPING_DEBOUNCE_MS, useDebounced } from '../lib/debounce';
import { formatDateTime, formatNumber, humanize } from '../lib/format';
import { translate, useT } from '../lib/i18n';
import type { AuditEvent, Paginated } from '../lib/types';

/** The entity kinds worth offering as a filter. */
const ENTITY_TYPES = [
  { value: '', label: 'audit.entity.all' },
  { value: 'user', label: 'audit.entity.user' },
  { value: 'wallet', label: 'audit.entity.wallet' },
  { value: 'alarm', label: 'audit.entity.alarm' },
  { value: 'alarm_event', label: 'audit.entity.alarm_event' },
  { value: 'reward_scheme', label: 'audit.entity.reward_scheme' },
  { value: 'payout_run', label: 'audit.entity.payout_run' },
  { value: 'notification_rule', label: 'audit.entity.notification_rule' },
  { value: 'notification_template', label: 'audit.entity.notification_template' },
  { value: 'notification_campaign', label: 'audit.entity.notification_campaign' },
  { value: 'blacklist', label: 'audit.entity.blacklist' },
  { value: 'referral_campaign', label: 'audit.entity.referral_campaign' },
  { value: 'referral_code', label: 'audit.entity.referral_code' },
  { value: 'referral', label: 'audit.entity.referral' },
  { value: 'referral_reward', label: 'audit.entity.referral_reward' },
  { value: 'backup', label: 'audit.entity.backup' },
];

/**
 * Everything the operators did.
 *
 * The third log, beside what the users did and what their money did. Between
 * the three, any number on any screen in this console can be traced back to
 * whatever caused it — which is the entire reason every mutating endpoint on
 * the API insists on a written reason before it will do anything.
 */
export function AuditPage() {
  const t = useT();
  const [filters, setFilters] = useState<{
    actor?: string;
    entityType?: string;
    deviceId?: string;
  }>({});
  const [page, setPage] = useState(1);
  // The operator and device boxes are typed a character at a time; one
  // request per pause, not per keystroke.
  const typed = useDebounced(filters, TYPING_DEBOUNCE_MS);

  const query = useMemo(
    () => compact({ ...typed, page, limit: 50 }),
    [typed, page],
  );

  const events = useQuery({
    queryKey: ['audit', query],
    queryFn: () => api.get<Paginated<AuditEvent>>('/api/audit', query),
  });

  const set = (next: Partial<typeof filters>) => {
    setFilters((current) => ({ ...current, ...next }));
    setPage(1);
  };

  return (
    <>
      <PageHeader title={t('audit.title')} description={t('audit.description')} />

      <div className="space-y-4">
        <div className="grid gap-3 rounded-xl border border-line bg-surface p-3.5 sm:grid-cols-3 sm:p-4">
          <Field label={t('audit.filter.operator')}>
            <Input
              value={filters.actor ?? ''}
              onChange={(event) => set({ actor: event.target.value || undefined })}
              placeholder={t('audit.filter.operatorPlaceholder')}
            />
          </Field>
          <Field label={t('audit.filter.entity')}>
            <Select
              value={filters.entityType ?? ''}
              onChange={(event) =>
                set({ entityType: event.target.value || undefined })
              }
            >
              {ENTITY_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.label)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('audit.filter.user')}>
            <Input
              className="font-mono text-xs"
              value={filters.deviceId ?? ''}
              onChange={(event) =>
                set({ deviceId: event.target.value.trim() || undefined })
              }
              placeholder={t('common.pasteDeviceId')}
            />
          </Field>
        </div>

        {events.isError && <ErrorNote error={events.error} />}

        <Card>
          {events.isPending ? (
            <Skeleton className="h-96" />
          ) : (events.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              icon={<ScrollText size={28} />}
              title={t('audit.emptyTitle')}
              description={t('audit.emptyDescription')}
            />
          ) : (
            <>
              <ul className="divide-y divide-line">
                {events.data?.items.map((event) => (
                  <AuditRow key={event.id} event={event} />
                ))}
              </ul>
              <Pagination
                page={events.data?.page ?? 1}
                totalPages={events.data?.totalPages ?? 1}
                total={events.data?.total ?? 0}
                onChange={setPage}
              />
            </>
          )}
        </Card>
      </div>
    </>
  );
}

function AuditRow({ event }: { event: AuditEvent }) {
  const t = useT();

  return (
    <li className="px-4 py-3.5 sm:px-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-ink">
            {humanize(event.action)}
          </span>
          <Badge tone="neutral">{humanize(event.entityType)}</Badge>
          {event.amount !== 0 && (
            <Badge tone={event.amount > 0 ? 'good' : 'danger'}>
              {event.amount > 0 ? '+' : '−'}
              {formatNumber(Math.abs(event.amount))}{' '}
              {t('common.creditSuffix')}
            </Badge>
          )}
        </div>
        <span className="text-xs text-ink-3">
          {event.actor} · {formatDateTime(event.occurredAt)}
        </span>
      </div>

      {event.reason && (
        <p className="mt-1 text-sm text-ink-2 break-anywhere">{event.reason}</p>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-3">
        {event.userId && (
          <span className="inline-flex items-center gap-1">
            {t('audit.userPrefix')} <IdChip id={event.userId} />
          </span>
        )}
        {event.ipAddress && (
          <span>{t('audit.from', { ip: event.ipAddress })}</span>
        )}
      </div>

      {event.changes && Object.keys(event.changes).length > 0 && (
        <Changes changes={event.changes} />
      )}
    </li>
  );
}

/**
 * The diff, as sentences.
 *
 * "changed balance 40 → 140" is what someone needs a year later; a pair of
 * full JSON snapshots would bury it. The backend stores only the fields that
 * actually moved, so everything here is worth reading.
 */
function Changes({ changes }: { changes: Record<string, [unknown, unknown]> }) {
  return (
    <ul className="mt-2 space-y-0.5 rounded-lg bg-raised px-2.5 py-1.5">
      {Object.entries(changes).map(([field, [before, after]]) => (
        <li key={field} className="text-xs break-anywhere">
          <span className="text-ink-2">{humanize(field)}</span>{' '}
          <span className="font-mono text-ink-3">{render(before)}</span>
          <span className="mx-1 text-ink-3">→</span>
          <span className="font-mono text-ink">{render(after)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * One side of a diff, as a short readable string.
 *
 * The backend already reduces objects and dates to JSON before storing them,
 * so anything arriving here that is still an object came from an older row —
 * naming it is more honest than rendering "[object Object]".
 */
function render(value: unknown): string {
  if (value === null || value === undefined) return translate('audit.valueNone');
  if (typeof value === 'boolean') {
    return translate(value ? 'audit.valueYes' : 'audit.valueNo');
  }
  if (typeof value === 'number') return String(value);

  const text =
    typeof value === 'string' ? value : (JSON.stringify(value) ?? '(value)');
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}
